import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  InputNumber,
  Modal,
  Space,
  Popconfirm,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  apiFixPriceAdjustment,
  apiPreviewPriceAdjustmentFix,
} from "api/apiAdmin";

// "1-for-20 reverse split"-style text for a detected ratio (a hint only)
const ratioText = (ratio) => {
  if (!ratio) return null;
  for (let d = 1; d <= 20; d++) {
    const n = ratio * d;
    if (Math.abs(n - Math.round(n)) < 1e-6) {
      return ratio >= 1
        ? `${Math.round(n)}-for-${d} split`
        : `${Math.round(n)}-for-${d} reverse split`;
    }
  }
  return `x${ratio}`;
};

const fmt = (obj = {}) =>
  Object.entries(obj)
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");

// Preview of a symbol's split fix (read-only), then the user starts it
const SplitFixModal = (props) => {
  const { symbol, onClose, onStarted } = props;
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [runDaily, setRunDaily] = useState(true);
  const [starting, setStarting] = useState(false);
  // per record: { oldShares, newShares } as the admin typed them
  const [shares, setShares] = useState({});

  const ratios = Object.fromEntries(
    Object.entries(shares)
      .filter(([, s]) => s.oldShares > 0 && s.newShares > 0)
      .map(([id, s]) => [id, s.newShares / s.oldShares]),
  );
  // per record: first day in the new prices, when the admin corrected it
  const [dates, setDates] = useState({});
  const ratiosKey = JSON.stringify({ ratios, dates });

  // the preview (trades after) follows the entered ratios
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = JSON.parse(ratiosKey);
      apiPreviewPriceAdjustmentFix(symbol, params.ratios, params.dates)
        .then((data) => {
          setPlan(data);
          setError(null);
        })
        .catch((err) => setError(err.response?.data?.message || err.message));
    }, 300);
    return () => clearTimeout(timer);
  }, [symbol, ratiosKey]);

  const setRecordShares = (id, key, value) =>
    setShares((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const handleFix = async () => {
    setStarting(true);
    try {
      await apiFixPriceAdjustment(symbol, runDaily, ratios, dates);
      message.success(`Fix for ${symbol} started -- follow it in the list`);
      onStarted();
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    } finally {
      setStarting(false);
    }
  };

  const tradeColumns = [
    { title: "Trade", dataIndex: "tradeId" },
    {
      title: "Status",
      render: (_, t) => `${t.accountType} ${t.orderStatus}`,
    },
    {
      title: "Opened",
      dataIndex: "created",
      render: (text) => dayjs(text).format("DD/MM/YY"),
    },
    { title: "x shares", dataIndex: "ratio", render: (r) => +r.toFixed(6) },
    {
      title: "Before",
      dataIndex: "before",
      render: (before) => <small>{fmt(before)}</small>,
    },
    {
      title: "After",
      render: (_, t) => (
        <small>
          {fmt(t.after)}
          {t.warnings.map((w) => (
            <div key={w} className="yellow">
              {w}
            </div>
          ))}
        </small>
      ),
    },
  ];

  const refreshedStrategies = plan?.strategies.filter((s) => s.refresh.length);
  const missing = plan?.missingRatios.length > 0;

  return (
    <Modal
      title={`Fix ${symbol}`}
      open
      onCancel={onClose}
      width={1200}
      destroyOnClose
      footer={[
        <Checkbox
          key="daily"
          checked={runDaily}
          onChange={(e) => setRunDaily(e.target.checked)}
          className="mr-4"
        >
          Run the daily strategy update for {symbol} after the fix
        </Checkbox>,
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Popconfirm
          key="fix"
          title={`Run the fix for ${symbol}?`}
          description="Writes candles (re-pull), predictions, trades and strategies as shown."
          onConfirm={handleFix}
          disabled={!plan || missing}
        >
          <Button
            type="primary"
            danger
            loading={starting}
            disabled={!plan || missing}
          >
            Run fix
          </Button>
        </Popconfirm>,
      ]}
    >
      {error && <Alert type="error" message={error} />}
      {!plan && !error && <Spin />}
      {plan && (
        <>
          <Descriptions size="small" column={1} bordered className="mb-4">
            <Descriptions.Item label="Events">
              {plan.records.map((r) => (
                <div key={r._id} className="mb-2">
                  <Tag>{r.status}</Tag>
                  {r.event}
                  {r.tradesTouched > 0 || dates[r._id] ? (
                    <div className="mt-1">
                      <Space wrap className="mb-1">
                        <span>First day in the new prices:</span>
                        <DatePicker
                          allowClear={false}
                          value={dayjs(r.effectiveDate || r.date)}
                          onChange={(d) =>
                            setDates((prev) => ({
                              ...prev,
                              [r._id]: d.format("YYYY-MM-DD"),
                            }))
                          }
                        />
                        <Typography.Text type="secondary">
                          (the split&apos;s ex-date -- values from before it
                          are adjusted; check it online)
                        </Typography.Text>
                      </Space>
                      <Space wrap>
                        <span>
                          Split ratio (changes {r.tradesTouched} open / pending
                          trade{r.tradesTouched === 1 ? "" : "s"}): every
                        </span>
                        <InputNumber
                          min={0}
                          placeholder="old"
                          value={shares[r._id]?.oldShares}
                          onChange={(v) => setRecordShares(r._id, "oldShares", v)}
                        />
                        <span>old shares became</span>
                        <InputNumber
                          min={0}
                          placeholder="new"
                          value={shares[r._id]?.newShares}
                          onChange={(v) => setRecordShares(r._id, "newShares", v)}
                        />
                        <span>new shares</span>
                      </Space>
                      <div>
                        <Typography.Text type="secondary">
                          {r.detectedRatio
                            ? `Detected: looks like a ${ratioText(r.detectedRatio)} -- verify it online, the detection can be off. `
                            : "Not a split ratio by the detection (spin-off / bad data?) -- check online. "}
                          Enter 1 and 1 if the share count didn&apos;t change.
                        </Typography.Text>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Typography.Text type="secondary">
                        From {r.effectiveDate || r.date} on. No open or pending
                        trade from before it -- no ratio needed (data repair
                        only)
                      </Typography.Text>
                    </div>
                  )}
                </div>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="1. Candles">
              {plan.repullNeeded
                ? "Still in the old prices: the full daily history is re-pulled from IBKR first (waits for it)"
                : plan.candles.some((c) => c.stillOld === null)
                  ? "Can't be checked for every event -- not re-pulled (check the chart if unsure)"
                  : "Already in the new prices"}
            </Descriptions.Item>
            <Descriptions.Item label="2. Predictions">
              Regenerated for {plan.models} model(s) from{" "}
              {plan.firstCandleDate} to {plan.lastClosedDay}, the last closed
              session (a failure stops the fix; {symbol} stays frozen)
            </Descriptions.Item>
            <Descriptions.Item label="3. Trades">
              {missing
                ? "Enter the split ratio above to see the adjusted trades"
                : plan.trades.length
                  ? `${plan.trades.length} open / pending trade(s) adjusted (below). Closed trades stay as executed.`
                  : "No open trades to adjust"}
            </Descriptions.Item>
            <Descriptions.Item label="4. Strategies">
              {refreshedStrategies.length
                ? refreshedStrategies.map((s) => (
                    <div key={s.strategyId}>
                      {s.name}: {s.refresh.join(", ")} re-read from the new
                      candles / predictions
                    </div>
                  ))
                : `No strategy prices from before the split (${plan.strategies.length} strateg${plan.strategies.length === 1 ? "y" : "ies"} on ${symbol})`}
            </Descriptions.Item>
            <Descriptions.Item label="5. Daily update">
              {runDaily
                ? `Catches up skipped daily runs with signals up to ${plan.lastClosedDay}. Today's unfinished candle is never used -- the 16:00 run handles today as usual.`
                : "Not run -- the next 16:00 run catches up"}
            </Descriptions.Item>
          </Descriptions>

          {plan.optionTrades.length > 0 && (
            <Alert
              type="warning"
              className="mb-4"
              message={`${plan.optionTrades.length} open option trade(s) are NOT adjusted (OCC changes the contracts) -- handle them at IBKR by hand`}
              description={plan.optionTrades
                .map(
                  (t) =>
                    `${t.strike}${t.optionType === "put" ? "P" : "C"} ${dayjs(t.expirationDate).format("DD/MM/YY")} x${t.amount} (${t.orderStatus})`,
                )
                .join(", ")}
            />
          )}

          {plan.trades.length > 0 && (
            <Table
              rowKey="tradeId"
              size="small"
              pagination={false}
              dataSource={plan.trades}
              columns={tradeColumns}
              scroll={{ x: 1000, y: 300 }}
            />
          )}
          <Typography.Paragraph type="secondary" className="mt-4">
            The fix runs in the background ({symbol} is frozen while it runs)
            and its progress shows in the list. AutoTrader then compares the IB
            position with the adjusted amounts and freezes {symbol} again if
            they still disagree.
          </Typography.Paragraph>
        </>
      )}
    </Modal>
  );
};

export default SplitFixModal;
